import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  App,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import PageHeader from "../../../components/PageHeader";
import {
  useGetSandboxResourcesQuery,
  useGetSandboxSupportedTypesQuery,
  useLinkSandboxResourceMutation,
  useUnlinkSandboxResourceMutation,
  useUpdateSandboxResourceMutation,
} from "../../../store/api/sandboxResourcesApi";
import { useGetResourcesQuery } from "../../../store/api/resourcesApi";
import { SandboxResourceRow } from "../../../services/sandbox/sandbox.types";

/**
 * Recursos de prueba (SCRUM-649).
 *
 * Publica recursos al sandbox de emmi-app, donde un tester los abre por su
 * cuenta, sin lección ni tarea alrededor. La lista es global: vincular un
 * recurso lo deja disponible para todos los testers.
 */
const SandboxResources: React.FC = () => {
  const { notification } = App.useApp();
  const {
    data: entries,
    isFetching,
    isError,
    refetch,
  } = useGetSandboxResourcesQuery();
  const { data: resources, isFetching: isFetchingResources } =
    useGetResourcesQuery();
  const { data: supportedTypesData, isFetching: isFetchingTypes } =
    useGetSandboxSupportedTypesQuery();

  const [linkResource, { isLoading: isLinking }] =
    useLinkSandboxResourceMutation();
  const [updateEntry] = useUpdateSandboxResourceMutation();
  const [unlinkEntry] = useUnlinkSandboxResourceMutation();

  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(
    null,
  );
  const [label, setLabel] = useState("");

  const linkedResourceIds = useMemo(
    () => new Set((entries ?? []).map((entry) => entry.resource_id)),
    [entries],
  );

  const supportedTypes = useMemo(
    () => new Set(supportedTypesData?.types ?? []),
    [supportedTypesData],
  );

  /**
   * Recursos disponibles: sólo los tipos que la app sabe abrir, y sin los que ya
   * están vinculados. Mientras la lista de tipos no llegó no se ofrece nada, en
   * vez de ofrecer todo y que el backend rechace al agregar.
   */
  const resourceOptions = useMemo(
    () =>
      (resources ?? [])
        .filter(
          (resource) =>
            !linkedResourceIds.has(resource.id) &&
            resource.type != null &&
            supportedTypes.has(resource.type),
        )
        .map((resource) => ({
          value: resource.id,
          label: `${resource.title}${resource.type ? ` · ${resource.type}` : ""}`,
        })),
    [resources, linkedResourceIds, supportedTypes],
  );

  const handleLink = async () => {
    if (selectedResourceId === null) return;

    try {
      await linkResource({
        resource_id: selectedResourceId,
        ...(label.trim() ? { label: label.trim() } : {}),
      }).unwrap();
      setSelectedResourceId(null);
      setLabel("");
      notification.success({
        message: "Recurso agregado",
        description: "Ya está disponible en la app para probar.",
      });
    } catch (error) {
      // El backend rechaza los tipos que la app no sabe abrir y los duplicados
      // con un mensaje concreto; mostrarlo es más útil que un texto genérico.
      const backendMessage = (
        error as { data?: { message?: string | string[] } }
      )?.data?.message;
      notification.error({
        message: "No se pudo agregar el recurso",
        description: Array.isArray(backendMessage)
          ? backendMessage.join(" ")
          : (backendMessage ??
            "Revisá que el recurso siga existiendo y volvé a intentar."),
      });
    }
  };

  const handleToggleEnabled = async (row: SandboxResourceRow) => {
    try {
      await updateEntry({ id: row.id, enabled: !row.enabled }).unwrap();
    } catch (error) {
      notification.error({
        message: "No se pudo cambiar el estado",
      });
    }
  };

  const handleUnlink = async (row: SandboxResourceRow) => {
    try {
      await unlinkEntry(row.id).unwrap();
      notification.success({ message: "Recurso quitado de las pruebas" });
    } catch (error) {
      notification.error({ message: "No se pudo quitar el recurso" });
    }
  };

  const columns: ColumnsType<SandboxResourceRow> = [
    {
      title: "Recurso",
      dataIndex: ["resource", "title"],
      key: "title",
      render: (_value, row) => row.resource?.title ?? "—",
    },
    {
      title: "Tipo",
      dataIndex: ["resource", "type"],
      key: "type",
      width: 180,
      render: (_value, row) =>
        row.resource?.type ? <Tag>{row.resource.type}</Tag> : "—",
    },
    {
      title: "Nota para el tester",
      dataIndex: "label",
      key: "label",
      render: (value: string | null) =>
        value ? (
          value
        ) : (
          <Typography.Text type="secondary">Sin nota</Typography.Text>
        ),
    },
    {
      title: "Visible en la app",
      dataIndex: "enabled",
      key: "enabled",
      width: 150,
      render: (enabled: boolean, row) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggleEnabled(row)}
          checkedChildren="Sí"
          unCheckedChildren="No"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 120,
      render: (_value, row) => (
        <Popconfirm
          title="¿Quitar de las pruebas?"
          description="El recurso deja de aparecer en la app. No se borra el recurso."
          okText="Quitar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleUnlink(row)}
        >
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Recursos de prueba" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cómo funciona"
        description="Los recursos que agregues acá aparecen en la app cuando un administrador inicia sesión. Sólo se pueden asignar los tipos que la app sabe abrir: juegos, quizzes, videos y lecturas. Nada de lo que hagan los testers se guarda: no genera puntos, no completa tareas y no queda en el reporte del alumno."
      />

      <Card title="Agregar un recurso" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: "100%" }} block>
          <Select
            showSearch
            allowClear
            placeholder="Buscar un recurso por nombre"
            style={{ width: "45%" }}
            value={selectedResourceId}
            onChange={(value) => setSelectedResourceId(value ?? null)}
            options={resourceOptions}
            loading={isFetchingResources || isFetchingTypes}
            notFoundContent={
              isFetchingResources || isFetchingTypes
                ? "Cargando…"
                : "Sin recursos disponibles de un tipo que la app pueda abrir"
            }
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          <Input
            placeholder="Nota opcional: qué hay que probar"
            style={{ width: "40%" }}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            maxLength={512}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleLink}
            loading={isLinking}
            disabled={selectedResourceId === null}
          >
            Agregar
          </Button>
        </Space.Compact>
      </Card>

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los recursos de prueba"
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Card>
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={entries ?? []}
            loading={isFetching}
            pagination={false}
            locale={{
              emptyText: (
                <Empty description="Todavía no hay recursos para probar" />
              ),
            }}
          />
        </Card>
      )}
    </>
  );
};

export default SandboxResources;
